import Intercom from "@intercom/messenger-js-sdk";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import {
  CollectionReference,
  DocumentData,
  DocumentReference,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  writeBatch
} from "firebase/firestore";
import { getBlob, ref } from "firebase/storage";
import mammoth from "mammoth";
import React from "react";
import { useSelector } from "react-redux";
import { FaFolder } from "@/components/icons/legacy";
import { StatusBadge } from "@/components/ui/status-badge";
import { db, storage } from "../api/firebaseApi";
import { criteriaDocuments } from "../constants/strings";
import { VisaDocumentType } from "../redux/documents/types";
import { lawyerSelectors } from "../redux/lawyer/selectors";
import { ExtractedPassport } from "../types/extracted-passport";
import { QualityIssue } from "../types/quality-issue";
import { getDocumentWarnings } from "../types/document-warnings";
import { DataDocs, DocumentType, OverviewStep } from "../types/tables-data";
import { VISAVALUE, isEmploymentBasedVisa } from "../types/visa.type";

export const getStatusValue = (steps: OverviewStep[]) => {
  let status = 1;
  steps.forEach((step) => {
    if (step.isComplete) {
      status += 1;
    }
  });
  return status;
};

const countries = [
  "usa",
  "united states",
  "united states of america",
  "canada",
  "philippines"
];
const countriesWithDashDates = ["tunisia"];
const datesToFormat = ["doB", "dateOfIssue", "expirationDate"];

export const formatPassport = (data: ExtractedPassport) => {
  const formattedPassportData: Record<string, string | number> = {};
  const isNotUsualFormatPassort =
    data.passportCountry &&
    (!countries.includes(data.passportCountry.toLowerCase()) ||
      !countriesWithDashDates.includes(data.passportCountry.toLowerCase()));

  if (isNotUsualFormatPassort) {
    Object.entries(data).forEach(([key, value]) => {
      if (datesToFormat.includes(key)) {
        const formatted = value.split(" ");
        const dateDoc = formatted[0];
        const monthDoc = formatted[1];
        const yearDoc = formatted[2];

        const dateFormatted = dayjs(
          `${yearDoc}-${monthDoc}-${dateDoc}`,
          "YYYY-MMM-DD"
        ).format("YYYY-MM-DD");
        formattedPassportData[key] = Date.parse(dateFormatted);
      } else {
        formattedPassportData[key] = value;
      }
    });
  }
  if (
    data.passportCountry &&
    countriesWithDashDates.includes(data.passportCountry.toLowerCase())
  ) {
    Object.entries(data).forEach(([key, value]) => {
      if (datesToFormat.includes(key)) {
        const formatted = value.split("-");
        const dateDoc = formatted[0];
        const monthDoc = formatted[1];
        const yearDoc = formatted[2];

        const dateFormatted = dayjs(
          `${yearDoc}-${monthDoc}-${dateDoc}`,
          "YYYY-MMM-DD"
        ).format("YYYY-MM-DD");
        formattedPassportData[key] = Date.parse(dateFormatted);
      } else {
        formattedPassportData[key] = value;
      }
    });
  }

  return formattedPassportData;
};

export const getAuthToken = () => {
  try {
    const token = localStorage.getItem("token");
    return token ? `Bearer ${token}` : null;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export const documentsTypes = {
  i94: "i94",
  resume: "resume",
  passport: "passport",
  visa: "visa"
};

// Exhibits
export const getNextExhibitValue = async (
  db: any, // Assuming db is already initialized and passed here
  collectionPath: CollectionReference<DocumentData>,
  type: "alphabetical" | "numerical"
): Promise<string | number> => {
  const q = query(collectionPath, orderBy("exhibit", "desc"), limit(1));
  const querySnapshot = await getDocs(q);

  let lastExhibitValue: string | number = type === "alphabetical" ? "A" : 1;

  if (!querySnapshot.empty) {
    const lastDoc = querySnapshot.docs[0];
    const lastExhibitData = lastDoc.data().exhibit;

    if (type === "alphabetical") {
      if (typeof lastExhibitData === "string") {
        const lastCharCode = lastExhibitData.charCodeAt(0);

        if (lastCharCode < 65 || lastCharCode > 90) {
          const error =
            "Exhibit value is not a valid uppercase ASCII character.";
          Sentry.captureException(new Error(error));
          throw new Error(error);
        }

        if (lastCharCode === 90) {
          const error = 'Reached the last exhibit label "Z".';
          Sentry.captureException(new Error(error));
          throw new Error(error);
        }

        lastExhibitValue = String.fromCharCode(lastCharCode + 1);
        return lastExhibitValue;
      }
      const error = "Exhibit value is not a string.";
      Sentry.captureException(new Error(error));
      throw new Error(error);
    } else if (type === "numerical") {
      if (typeof lastExhibitData === "number") {
        lastExhibitValue = lastExhibitData + 1;
        return lastExhibitValue;
      }
      const error = "Exhibit value is not a number.";
      Sentry.captureException(new Error(error));
      throw new Error(error);
    }
  }

  return lastExhibitValue;
};

export const incExhibit = (exhibit: string): string => {
  const lastChar = exhibit.slice(-1);
  const prefix = exhibit.slice(0, -1);

  if (lastChar === "Z") {
    return prefix.length === 0 ? "AA" : `${incExhibit(prefix)}A`;
  }

  return prefix + String.fromCharCode(lastChar.charCodeAt(0) + 1);
};

// Documents files helpers

export const openFilePreview = (docUrls?: string) => {
  if (docUrls) {
    window.open(docUrls, "_blank");
  }
};

export type GenerateDocumentsProps = {
  documents: {
    id: string;
    filePath: string;
    super_class: string;
    criterion: string;
  }[];
  mainDocId?: string;
};

export const fetchFiles = async (filePaths: string[]): Promise<string[]> => {
  const fileUrls: string[] = [];
  try {
    const promises = filePaths.map(async (filePath) => {
      const fileRef = ref(storage, filePath);
      const fileBlob = await getBlob(fileRef);
      const fileUrl = URL.createObjectURL(fileBlob);
      fileUrls.push(fileUrl);
    });
    await Promise.all(promises);
    return fileUrls;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

// Utility function to wrap text and render it on a canvas
const wrapText = (
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
) => {
  const words = text.split(" ");
  let line = "";

  for (let i = 0; i < words.length; i += 1) {
    const testLine = `${line + words[i]} `;
    const testWidth = context.measureText(testLine).width;
    if (testWidth > maxWidth && i > 0) {
      context.fillText(line, x, y);
      line = `${words[i]} `;
      y += lineHeight;
    } else {
      line = testLine;
    }
  }
  context.fillText(line, x, y);
};

export const generateDocxThumbnail = async (
  docxUrl: string
): Promise<string> => {
  // Fetch the DOCX file as an ArrayBuffer
  const response = await fetch(docxUrl);
  const arrayBuffer = await response.arrayBuffer();

  // Use mammoth.js to extract text from the DOCX file
  const result = await mammoth.extractRawText({ arrayBuffer });
  const text = result.value; // Extracted text
  const firstPageText = text.split("\n").slice(0, 80).join("\n");

  // Define thumbnail dimensions (similar to A4)
  const canvasWidth = 595;
  const canvasHeight = 841;

  // Create canvas element
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Unable to create canvas context.");
  }
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  // Style for rendering text on the canvas
  context.fillStyle = "#ffffff"; // Background color (white)
  context.fillRect(0, 0, canvasWidth, canvasHeight); // Fill background
  context.fillStyle = "#000000"; // Text color (black)
  context.font = "16px Arial"; // Font settings
  context.textAlign = "left";
  context.textBaseline = "top";

  // Render the first page's text content to the canvas
  const padding = 20;
  const lineHeight = 20;
  const maxTextWidth = canvasWidth - padding * 2;
  wrapText(context, firstPageText, padding, padding, maxTextWidth, lineHeight);

  // Convert canvas to PNG Blob and return the thumbnail URL
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        const thumbnail = URL.createObjectURL(blob);
        resolve(thumbnail);
      } else {
        reject(new Error("Could not create blob from canvas"));
      }
    }, "image/png");
  });
};

export const openInNewTab = (fileUrl: string, fileName: string) => {
  window.open(fileUrl, fileName);
};

// status, labels

export const documentStatusLabel = (uploadBy: string) => {
  if (uploadBy == null || uploadBy === "") return "Not uploaded";
  return `Uploaded by ${uploadBy}`;
};

// get user type
export const getUserType = (): string => {
  const loginType = localStorage.getItem("loginType") ?? "";
  return loginType;
};

// mapping
export const getKeyByValue = (object: any, value: any) =>
  Object.keys(object).find((key) => object[key] === value);

export const getTypesForCriterion = (
  criterionCategory?: string | null,
  visaType?: string | null
) => {
  if (!criterionCategory || !visaType) {
    return null;
  }

  const criterion = criteriaDocuments[visaType!]?.find(
    (c) => c?.category === criterionCategory
  );
  const criterionCategories = criterion?.documentTypes?.map(
    (doc) => doc?.title
  );
  return criterion ? criterionCategories : null;
};

export const addPropertiesSafely = <T extends object>(
  target: any,
  source: any
): T => {
  const newObject = { ...target }; // Create a shallow copy of target
  Object.keys(source).forEach((key) => {
    const value = source[key as keyof T];
    if (value !== undefined && value !== null) {
      newObject[key as keyof T] = value;
    }
  });
  return newObject;
};

export const isSuperAdmin = (): boolean => {
  const role = useSelector(lawyerSelectors.selectRole);

  return role === "superadmin";
};

export const isFullUser = (): boolean => {
  const role = useSelector(lawyerSelectors.selectRole);

  return role === "full";
};

export function isDocumentType(type: any): type is DocumentType {
  return typeof type === "object" && type !== null && "title" in type;
}

export const hasId = (item: unknown): item is { id: string | number } =>
  typeof item === "object" && item !== null && "id" in item;

export const truncateText = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return `${text.substring(0, maxLength)}...`;
  }
  return text;
};

// Firebase

/**
 * Generates a new document ID from the specified Firestore collection path.
 *
 * @param db - The Firestore database instance.
 * @param collectionPath - The path to the Firestore collection.
 * @return The generated document ID.
 */
export const generateNewDocId = (collectionPath: string): string => {
  const newDocRef = doc(collection(db, collectionPath));
  return newDocRef.id;
};

export const sleep = (duration: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, duration);
  });

// export const toDate

export const showCTAButton = (indivId: string | null): boolean => {
  return indivId === "" || indivId === null;
};

export const isValidPhoneNumber = (value: string): boolean => {
  return value !== "+1" && value !== "";
};

const INTERCOM_DISABLED_HOSTS = new Set(["beta.caseblink.com"]);

export const SUPPORT_EMAIL = "support@caseblink.com";
/** The app sidebar's expanded width (`SIDEBAR_WIDTH`, 16rem). */
const SIDEBAR_WIDTH_PX = 256;

let supportUnread = 0;
const supportUnreadListeners = new Set<() => void>();

/** Unread replies from CaseBlink Support (Intercom's `onUnreadCountChange`). */
export const getSupportUnread = (): number => supportUnread;

export const subscribeSupportUnread = (listener: () => void): (() => void) => {
  supportUnreadListeners.add(listener);
  return () => {
    supportUnreadListeners.delete(listener);
  };
};

const setSupportUnread = (count: unknown): void => {
  const next = typeof count === "number" && count > 0 ? count : 0;
  if (next === supportUnread) return;
  supportUnread = next;
  supportUnreadListeners.forEach((listener) => listener());
};

/**
 * Whether the Messenger actually booted: the SDK's stub queues calls (and
 * keeps a `q`) until the widget script loads, which a disabled host or an ad
 * blocker never lets happen.
 */
export const isSupportChatAvailable = (): boolean => {
  if (typeof window === "undefined") return false;
  const intercom = window.Intercom as
    | (((...args: unknown[]) => unknown) & { booted?: boolean; q?: unknown })
    | undefined;
  if (typeof intercom !== "function") return false;
  return intercom.booted === true || !("q" in intercom);
};

/**
 * Open the support Messenger: on its home, or on a new message prefilled with
 * `prefill` (an empty string opens a blank one). The person can edit it and
 * presses Send themselves. Without the Messenger, an email to support.
 */
export const openSupport = (prefill?: string): void => {
  if (isSupportChatAvailable() && window.Intercom) {
    if (prefill === undefined) window.Intercom("show");
    else window.Intercom("showNewMessage", prefill);
    return;
  }
  const body = prefill?.trim() ? `?body=${encodeURIComponent(prefill)}` : "";
  window.location.href = `mailto:${SUPPORT_EMAIL}${body}`;
};

export const setupIntercom = (email: string, uid: string): void => {
  if (
    typeof window !== "undefined" &&
    INTERCOM_DISABLED_HOSTS.has(window.location.hostname)
  ) {
    return;
  }

  if (import.meta.env.REACT_APP_INTERCOM_APP_ID !== undefined) {
    setTimeout(() => {
      // No floating launcher: support opens from the sidebar's Support row,
      // the account menu and the assistant's handoff card, beside the sidebar
      // and clear of the panel.
      Intercom({
        app_id: import.meta.env.REACT_APP_INTERCOM_APP_ID!,
        user_id: uid,
        email,
        hide_default_launcher: true,
        alignment: "left",
        horizontal_padding: SIDEBAR_WIDTH_PX + 12
      });
      if (window !== undefined && window.Intercom !== undefined) {
        window.Intercom("update");
        window.Intercom("onUnreadCountChange", setSupportUnread);
      }
    }, 3000);
  }
};

export const mapVisaTypeToPath = (visaType: string) => {
  if (visaType === "O-1-A") return "O-1A";
  if (visaType === "EB-2-NIW") return "EB-2 NIW";
  if (visaType === "EB-2-NIW-v2") return "EB-2 NIW v2";
  if (visaType === "EB-2-NIW-v2") return "EB-2 NIW v2";
  if (visaType === "O-1-B") return "O-1B";

  return visaType;
};

export const getDocumentTypesByTypeVisaCategory = (
  map: any,
  docType: VisaDocumentType,
  visaType: string | null | undefined = null,
  category: string | null | undefined = null
) => {
  if (map) {
    // signed-expert-letters needs to be signed_expert_letters
    const docTypeStr = docType.replace(/-/g, "_");

    if (docTypeStr in map) {
      let retVal = map[docTypeStr];

      // != treats null and undefined as the same - what is WRONG with you Javascript?
      if (visaType != null) {
        retVal = retVal[visaType];
      }

      // != treats null and undefined as the same - what is WRONG with you Javascript?
      if (category != null) {
        if (category in retVal) {
          retVal = retVal[category];
        } else {
          retVal = [""];
        }
      }
      return retVal;
    }
    return [""];
  }
  return [""];
};

export const getStoragePathByVisaType = (
  visaType: VisaDocumentType,
  uid: string
) => {
  switch (visaType) {
    case VisaDocumentType.Standard:
      return `individuals/documents/${uid}/`;
    case VisaDocumentType.Evidence:
      return `individuals/documents/${uid}/Evidences`;
    case VisaDocumentType.SignedExpertLetters:
      return `individuals/documents/${uid}/signed_expert_letters`;
    default:
      return `individuals/documents/${uid}/`;
  }
};

export const getSubdomain = () => {
  const { hostname } = window.location;
  const subdomain = hostname.split(".")[0];
  return subdomain;
};

export const delay = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve(1);
    }, ms);
  });

export const inDev = () => {
  if (process.env.NODE_ENV === "development") {
    return true;
  }
  return false;
};

export const getQualityBadge = (docData: DataDocs): React.ReactNode => {
  if (!docData) return null;
  const warnings = getDocumentWarnings(docData);
  if (!docData.last_updated_at && warnings.length > 0) {
    if (warnings.includes(QualityIssue.InsufficientInformation)) {
      return React.createElement(
        StatusBadge,
        { tone: "danger" },
        "Needs Update"
      );
    }
    return React.createElement(
      StatusBadge,
      { tone: "orange" },
      "Needs More Information"
    );
  }
  return null;
};

/**
 * Checks if the visa type should show template info options (area of expertise, employer details).
 * Only shown for employment-based / Business Immigration workflows.
 * @param visaType - The visa type to check
 * @returns true if the visa type should show template info options, false otherwise
 */
export const shouldShowTemplateInfoOptions = (
  visaType: VISAVALUE | undefined
): boolean => {
  return isEmploymentBasedVisa(visaType);
};
